package de.oevbs.kunde.kundenportal2.services.contract

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.businesspartner.bpcrmservice2.contract.model.Contract
import de.oevbs.businesspartner.bpcrmservice2.contract.model.ContractDetails
import de.oevbs.businesspartner.bpcrmservice2.contract.model.isNotActive
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.authentication.PortalSessionMiddleware
import de.oevbs.kunde.kundenportal2.components.globalInitTracker
import de.oevbs.kunde.kundenportal2.model.services.contract.ContractClientDefinition
import de.oevbs.kunde.kundenportal2.model.services.contract.ContractFilter
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import de.oevbs.sfa.communication.sfaServiceOrigin
import de.oevbs.sfa.communication.use
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.storeOf
import io.ktor.client.*
import io.ktor.client.engine.js.*
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.*

/**
 * Die Stores für Inbox, Vertragsdetails und Schäden sind die einzigen der "Selfservices" Stores, die als object angelegt
 * werden sollten. da keine Schritte/Ändern von Daten passiert und damit die Daten vorhanden bleiben können.
 * Alle anderen Stores bitte als Klassen anlegen und in der Haupt-Renderfunktion des jeweiligen Self-Services
 * instantiieren. Dies hat den großen Vorteil, dass alle Submit-Daten, Schritte usw. mit der Variablen gelöscht werden
 * und ein Self-Service immer mit frischen Daten aus dem Backend initialisiert ist.
 */

object ContractClient : ContractClientDefinition(window.location.sfaServiceOrigin) {
    override val client: HttpClient = HttpClient(Js, config)

    init {
        use(PortalSessionMiddleware)
    }
}

object FilterStore : RootStore<ContractFilter>(ContractFilter.emptyFilter, Job()) {
    val cancelledChanges: Flow<Unit> = data.map { it.showArchived }.distinctUntilChanged().map { }

    init {
        PortalSession.onLogout { update(ContractFilter.emptyFilter) }
    }
}

class ContractStore(job: Job) : RootStore<List<Contract>>(emptyList(), job) {

    sealed interface Mode {
        data object List : Mode

        data class Details(val contractNumber: String) : Mode
    }

    private val storedMode = storeOf<Mode>(Mode.List, job)
    val mode: Flow<Mode> = storedMode.data
    val showList: SimpleHandler<Unit> = storedMode.handle { Mode.List }
    val showDetails: SimpleHandler<String> = storedMode.handle { _, number -> Mode.Details(number) }

    /**
     * Dieser spezielle Datenstrom filtert die Verträge hinsichtlich der Spartenauswahl (Selectbox im UI)
     * und setzt die Liste anhand des Storno-Status neu zusammen: Erst kommen alle aktiven (blauen)
     * Verträge, dann alle nicht aktiven (grauen).
     */
    val filteredAndSequenced = combine(data, FilterStore.data.map { it.lineOfBusiness }) { list, selectedLob ->
        // `LineOfBusiness.Unbekannt` wird für den Filter "Alle Sparten" verwendet, um keinen eigenen Enum Typen
        // bauen zu müssen. vgl. `lineOfBusinessFilter` und dort das Setzen des Textes des Buttons!
        list.filter { it.lineOfBusiness == selectedLob || selectedLob == LineOfBusiness.Unbekannt }
    }.map { contracts ->
        contracts.partition { contract -> contract.status.isNotActive }
            .let { (nichtAktiv, aktiv) -> aktiv + nichtAktiv }
    }

    private val initFailedStore = storeOf(false, job)
    val initFailedFlow: Flow<Boolean> = initFailedStore.data

    val loadContracts = handle {
        globalInitTracker.track {
            val initData = tryCatchAndHandleErrors("Laden der Verträge") {
                ContractClient.contracts(FilterStore.current.showArchived)
            }?.contracts
            if (initData == null) {
                initFailedStore.update(true)
                return@track emptyList()
            }
            initData
        }
    }

    init {
        FilterStore.cancelledChanges handledBy loadContracts
    }
}

object DetailsStore : RootStore<ContractDetails?>(null, Job()) {

    private val initFailedStore = storeOf(false, job)
    val initFailedFlow: Flow<Boolean> = initFailedStore.data

    val noneNullData: Flow<ContractDetails> = data.filterNotNull()

    val loadDetails = handle<String> { state, contractNumber ->
        globalInitTracker.track {
            val initData = tryCatchAndHandleErrors("Laden der Vertragsdetails") {
                ContractClient.contractDetail(contractNumber)
            }
            if (initData == null) {
                initFailedStore.update(true)
                return@track state
            }
            initData
        }
    }
}